import { RouterModule, Routes } from '@angular/router';
import { LogoutComponent } from './auth/logout/logout.component';
import { InternalAppComponent, PageNotFoundComponent } from '@fc-core';
import { AuthGuard } from './auth/auth-guard.service';
import { NgModule } from '@angular/core';
import { mapResolver } from './core/services/map-resolver.service';
import { hasPermissionGuard } from './core/guards/has-permission.guard';
import { FcPermissions } from '@fc-core';
import {
  IdlePreloadingStrategy,
  PRELOAD_WHEN_IDLE,
} from './core/routing/idle-preloading.strategy';

export const routes: Routes = [
  {
    path: 'components-overview',
    loadChildren: () =>
      import('./components-overview/components-overview.routes').then(
        (m) => m.routes,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'invitation/:key',
    loadComponent: () => import('./auth/invitation/invitation.component'),
  },
  {
    path: 's/:shareId',
    loadChildren: () =>
      import('./vehicle-sharing/vehicle-sharing.module').then(
        (m) => m.VehicleSharingModule,
      ),
    resolve: {
      map: mapResolver,
    },
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'logout',
    component: LogoutComponent,
  },
  {
    path: 'confirm/:token/:uid',
    redirectTo: 'auth/confirm/:token/:uid',
    pathMatch: 'full',
  },
  {
    path: 'confirm-email/:token/:uid',
    redirectTo: 'auth/confirm-email/:token/:uid',
    pathMatch: 'full',
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/dashboard',
  },
  {
    path: '',
    component: InternalAppComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'map',
        canActivate: [hasPermissionGuard(FcPermissions.mapFeature)],
        loadChildren: () => import('./map/map.module').then((m) => m.MapModule),
        resolve: {
          map: mapResolver,
        },
        data: {
          title: 'Map',
          quickActionType: 'vehicles',
        },
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
        data: {
          title: 'Dashboard',
          quickActionType: 'vehicles',
        },
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./settings/settings.module').then((m) => m.SettingsModule),
        data: {
          title: 'Settings',
          quickActionType: 'vehicles',
        },
      },
      {
        path: 'reports',
        loadChildren: () =>
          import('./reports/reports.module').then((m) => m.ReportsModule),
        data: {
          title: 'Reports',
          quickActionType: 'vehicles',
        },
      },
      {
        path: 'locations',
        loadChildren: () =>
          import('./locations/locations.module').then((m) => m.LocationsModule),
        resolve: {
          map: mapResolver,
        },
        data: {
          title: 'Locations',
          quickActionType: 'locations',
        },
      },
      {
        path: 'crm',
        loadChildren: () => import('./crm/crm.module').then((m) => m.CrmModule),
        data: {
          title: 'CRM',
        },
      },
      {
        path: 'vehicles',
        loadChildren: () =>
          import('./vehicles/vehicles.module').then((m) => m.VehiclesModule),
        data: {
          title: 'Vehicles',
          [PRELOAD_WHEN_IDLE]: true,
        },
      },
      {
        path: 'cameras',
        loadChildren: () =>
          import('./cameras/cameras.module').then((m) => m.CamerasModule),
        data: {
          title: 'Cameras',
          quickActionType: 'vehicles',
        },
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./employees/employees.module').then((m) => m.EmployeesModule),
        data: {
          title: 'Employees',
        },
      },
      {
        path: 'shared',
        loadChildren: () =>
          import('./live-share/live-share.module').then(
            (m) => m.LiveShareModule,
          ),
        data: {
          title: 'Live Share',
        },
      },
      {
        path: 'chats',
        loadChildren: () =>
          import('./chats/chats.module').then((m) => m.ChatsModule),
        data: {
          title: 'Chats',
          quickActionType: 'vehicles',
        },
      },
      {
        path: 'events',
        loadChildren: () =>
          import('./events/events.module').then((m) => m.EventsModule),
        data: {
          title: 'Events',
          quickActionType: 'events',
        },
      },
      {
        path: 'tasks',
        loadChildren: () =>
          import('./tasks/tasks.module').then((m) => m.TasksModule),
        data: {
          title: 'Task board',
          quickActionType: 'tasks',
        },
      },
      {
        path: 'forms',
        loadChildren: () =>
          import('./forms/forms.routes').then((m) => m.formsRoutes),
        data: {
          title: 'Forms',
        },
      },
      {
        path: 'fleet-assets',
        loadChildren: () =>
          import('./assets/assets.routes').then((m) => m.assetsRoutes),
        data: {
          title: 'Assets',
        },
      },
      {
        path: 'documents',
        loadChildren: () =>
          import('./documents/documents.routes').then((m) => m.documentsRoutes),
        data: {
          title: 'Documents',
          quickActionType: 'documents',
        },
      },
      {
        path: 'time-management',
        loadChildren: () =>
          import('./time-management/time-management.module').then(
            (m) => m.TimeManagementModule,
          ),
        data: {
          title: 'Time Management',
          quickActionType: 'vehicles',
        },
      },
      {
        path: 'projects',
        loadChildren: () =>
          import('./projects/projects.module').then((m) => m.ProjectsModule),
        data: {
          title: 'Projects',
          quickActionType: 'projects',
        },
      },
      {
        path: 'search',
        loadComponent: () =>
          import('./search/search.component').then((m) => m.SearchComponent),
        data: {
          title: 'Search',
          quickActionType: 'vehicles',
        },
      },
      {
        path: '**',
        component: PageNotFoundComponent,
      },
    ],
  },
  {
    path: '**',
    component: PageNotFoundComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      preloadingStrategy: IdlePreloadingStrategy,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
